import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '../supabase/supabase';

const eventTypes = [
  "Weddings",
  "Birthday Parties",
  "Quinceañeras",
  "Bar Mitzvahs",
  "Corporate Branding",
  "Holiday Galas",
  "Bridal Showers",
  "Charity Fundraisers",
  "Other"
];

const ContactForm = () => {
  const [status, setStatus] = useState('');
  const FORMSPREE_ID = "xwvroajw";

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());
    
    setStatus('sending');

    try {
      // 1. Save to Supabase
      const { error: dbError } = await supabase
        .from('contact_inquiries_1740051000000')
        .insert([{
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          phone: data.phone,
          event_type: data.eventType,
          event_date: data.date || null,
          message: data.message,
          status: 'new'
        }]);

      if (dbError) throw dbError;

      // 2. Send to Email via Formspree
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
      }
    } catch (err) {
      console.error("Submission error:", err);
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-parlor-green border-t border-parlor-gold/20">
      <div className="max-w-7xl mx-auto px-6 flex flex-col lg:flex-row gap-16">
        <div className="lg:w-5/12 text-parlor-cream">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-serif text-4xl md:text-5xl text-parlor-gold mb-6">Inquire About Your Event</h2>
            <p className="text-parlor-cream/80 text-lg mb-8 leading-relaxed">
              Ready to create memories that stick? Fill out the form to check availability.
            </p>
            <div className="space-y-4 text-parlor-cream/80">
              <p className="flex items-center gap-4">
                <span className="text-parlor-gold uppercase tracking-widest text-xs w-20">Email</span>
                <a href="mailto:hello@magnetparlor.com" className="hover:text-parlor-gold transition-colors">hello@magnetparlor.com</a>
              </p>
              <p className="flex items-center gap-4">
                <span className="text-parlor-gold uppercase tracking-widest text-xs w-20">Phone</span>
                (872) 704-0034
              </p>
            </div>
          </motion.div>
        </div>

        <div className="lg:w-7/12">
          <motion.div className="bg-parlor-green-dark p-8 md:p-10 shadow-2xl border border-parlor-gold/10">
            {status === 'success' ? (
              <SuccessState onReset={() => setStatus('')} />
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <Input label="First Name" name="firstName" required />
                  <Input label="Last Name" name="lastName" required />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <Input label="Email Address" name="email" type="email" required />
                  <Input label="Phone Number" name="phone" type="tel" required />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="flex flex-col">
                    <label className="block text-parlor-gold text-xs uppercase tracking-widest mb-2 font-semibold">Event Type</label>
                    <select 
                      name="eventType" 
                      required 
                      className="w-full bg-parlor-green-dark border-b border-parlor-cream/20 text-parlor-cream py-2 focus:outline-none focus:border-parlor-gold transition-colors appearance-none cursor-pointer"
                    >
                      <option value="" disabled selected>Select event type...</option>
                      {eventTypes.map(type => (
                        <option key={type} value={type} className="bg-parlor-green text-parlor-cream">{type}</option>
                      ))}
                    </select>
                  </div>
                  <Input label="Event Date" name="date" type="date" />
                </div>
                <div className="mb-8">
                  <label className="block text-parlor-gold text-xs uppercase tracking-widest mb-2 font-semibold">Event Details</label>
                  <textarea 
                    name="message" 
                    required 
                    rows="4" 
                    className="w-full bg-transparent border-b border-parlor-cream/20 text-parlor-cream py-2 focus:outline-none focus:border-parlor-gold transition-colors resize-none" 
                    placeholder="Details..."></textarea>
                </div>
                <button 
                  type="submit" 
                  disabled={status === 'sending'}
                  className="w-full bg-parlor-gold text-parlor-green font-bold uppercase tracking-[0.2em] py-4 hover:bg-parlor-cream transition-colors disabled:opacity-50"
                >
                  {status === 'sending' ? 'Sending...' : 'Submit Inquiry'}
                </button>
                {status === 'error' && <p className="mt-4 text-red-400 text-sm text-center">There was an error. Please try again.</p>}
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Input = ({ label, ...props }) => (
  <div>
    <label className="block text-parlor-gold text-xs uppercase tracking-widest mb-2 font-semibold">{label}</label>
    <input {...props} className="w-full bg-transparent border-b border-parlor-cream/20 text-parlor-cream py-2 focus:outline-none focus:border-parlor-gold transition-colors" />
  </div>
);

const SuccessState = ({ onReset }) => (
  <div className="py-12 text-center">
    <SafeIcon icon={FiIcons.FiCheckCircle} className="text-6xl text-parlor-gold mx-auto mb-6" />
    <h3 className="font-serif text-3xl text-parlor-cream mb-4">Inquiry Received</h3>
    <p className="text-parlor-cream/60 mb-8">We've sent you a confirmation email and will be in touch shortly.</p>
    <button onClick={onReset} className="text-parlor-gold uppercase tracking-widest text-xs font-bold border-b border-parlor-gold pb-1 hover:text-parlor-cream hover:border-parlor-cream transition-colors">Send another</button>
  </div>
);

export default ContactForm;